/**
 * FloatingFeedbackButton Component
 *
 * A floating feedback entry point on the right side of the viewport.
 * Features:
 * - Fixed position: right side, ~50% down (eye-tracking sweet spot)
 * - Moves to left side when ContractDetailsPanel is open (collision avoidance)
 * - Eye-catching idle state with subtle glow
 * - One-time "ping" animation on first page load (per session)
 * - Expands on hover to show "Send feedback" label
 * - Hidden on small screens (< md)
 * - Full light/dark theme support with Optioneer brand styling
 */

import { useState, useEffect } from 'react'
import { MessageSquareText } from 'lucide-react'
import { cn } from '../../utils/cn'
import { useAuth } from '../../contexts/AuthContext'
import FeedbackModal from './FeedbackModal'

const STORAGE_KEY = 'optioneer_feedback_ping_shown'

export default function FloatingFeedbackButton() {
  const { user } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showPing, setShowPing] = useState(false)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  // One-time attention animation per session
  useEffect(() => {
    if (!user) return

    // Check if ping was already shown this session
    const pingShown = sessionStorage.getItem(STORAGE_KEY)
    if (pingShown) return

    // Trigger ping after 1.5s delay
    const timer = setTimeout(() => {
      setShowPing(true)
      sessionStorage.setItem(STORAGE_KEY, 'true')

      // Remove ping class after animation completes
      setTimeout(() => setShowPing(false), 1000)
    }, 1500)

    return () => clearTimeout(timer)
  }, [user])

  // Detect ContractDetailsPanel open state
  useEffect(() => {
    const checkPanelOpen = () => {
      // Check for ContractDetailsPanel by looking for its container
      const panel = document.querySelector('[data-contract-panel]')
      setIsPanelOpen(!!panel)
    }

    // Check initially
    checkPanelOpen()

    // Set up a MutationObserver to detect when panel is added/removed
    const observer = new MutationObserver(checkPanelOpen)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  // Only show for authenticated users
  if (!user) return null

  return (
    <>
      {/* Floating Button - hidden on mobile, visible on md+ */}
      <button
        data-floating-feedback
        onClick={() => setIsModalOpen(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          // Positioning: switches side based on panel state
          'fixed top-[50%] -translate-y-1/2 z-[60]',
          'transition-all duration-200 ease-out',
          isPanelOpen ? 'left-4' : 'right-4',
          // Hidden on mobile
          'hidden md:flex',
          // Base styles
          'items-center gap-2 px-3.5 py-2.5 rounded-full',
          // Light theme - stronger idle state
          'bg-white border-2 border-brand-200/80 text-brand-600',
          'shadow-lg shadow-brand-500/15',
          // Light hover
          'hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700',
          'hover:shadow-xl hover:shadow-brand-500/25',
          // Dark theme - stronger idle state
          'dark:bg-slate-800 dark:border-amber-500/40 dark:text-amber-400',
          'dark:shadow-lg dark:shadow-amber-500/10',
          // Dark hover
          'dark:hover:bg-amber-500/15 dark:hover:border-amber-500/60 dark:hover:text-amber-300',
          'dark:hover:shadow-xl dark:hover:shadow-amber-500/20',
          // Micro-interactions
          'hover:scale-105 active:scale-[0.98]',
          // Focus styles for accessibility
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-brand-500 dark:focus-visible:ring-amber-500',
          'focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
          // One-time nudge animation
          showPing && 'animate-[nudge_0.3s_ease-in-out]'
        )}
        aria-label="Send feedback"
        title="Bug or feature request"
      >
        {/* Diagonal sheen effect - subtle gradient overlay */}
        <span
          className={cn(
            'absolute inset-0 rounded-full overflow-hidden pointer-events-none',
            'before:absolute before:inset-0',
            'before:bg-gradient-to-br before:from-white/40 before:via-transparent before:to-transparent',
            'dark:before:from-white/10 dark:before:via-transparent dark:before:to-transparent'
          )}
        />

        {/* Gradient border glow on hover */}
        <span
          className={cn(
            'absolute inset-0 rounded-full opacity-0 transition-opacity duration-200',
            'bg-gradient-to-r from-brand-500/20 via-transparent to-brand-500/20',
            'dark:from-amber-500/20 dark:via-transparent dark:to-amber-500/20',
            isHovered && 'opacity-100',
            'pointer-events-none'
          )}
        />

        {/* Signal dot - always visible, ping animation once per session */}
        <span
          className={cn(
            'absolute -top-0.5 h-2.5 w-2.5 rounded-full',
            isPanelOpen ? '-left-0.5' : '-right-0.5',
            'bg-brand-500 dark:bg-amber-500',
            'shadow-sm shadow-brand-500/50 dark:shadow-amber-500/50',
            'transition-all duration-200',
            // Outer ping ring for one-time animation
            showPing &&
              'after:absolute after:inset-0 after:rounded-full after:bg-brand-500 dark:after:bg-amber-500 after:animate-ping'
          )}
        />

        {/* Icon */}
        <MessageSquareText
          className={cn(
            'h-5 w-5 transition-transform duration-150 relative z-10',
            isHovered && (isPanelOpen ? '-translate-x-0.5' : 'translate-x-0.5')
          )}
        />

        {/* Label - expands on hover */}
        <span
          className={cn(
            'text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-200 relative z-10',
            isHovered ? 'max-w-[100px] opacity-100' : 'max-w-0 opacity-0'
          )}
        >
          Send feedback
        </span>
      </button>

      {/* Custom keyframes for nudge animation */}
      <style>{`
        @keyframes nudge {
          0%, 100% { transform: translateY(-50%) translateX(0); }
          25% { transform: translateY(-50%) translateX(-2px); }
          75% { transform: translateY(-50%) translateX(2px); }
        }
      `}</style>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  )
}
